import { GAME_LOBBY_PROVIDER_ALL } from '~/constants'
import { GameLobbyEnum, KeyEnum, RoutesEnum } from '~/enums'
import { GAME_LOBBY_PROVIDERS, GAME_LOBBY_TABS } from '~/resources'

export function useLobbyGameFilter() {
  const route = useRoute()
  const router = useRouter()
  const { requestFetchGamesV2 } = useLobbyGames()

  const routeCategory = computed(() => {
    return (route.params?.cats as string) ?? GameLobbyEnum.All
  })

  const routeProvider = computed(() => {
    return (route.params?.prov as string) ?? ''
  })

  const { data: allGames } = useNuxtData(KeyEnum.AllGames)

  const requestGetAllGames = async () => {
    await useAsyncData(KeyEnum.AllGames, async () => {
      try {
        const responseGames = await requestFetchGamesV2({
          limit: -1,
        })
        return responseGames?.data ?? []
      } catch {
        return []
      }
    })
  }

  requestGetAllGames()

  const getImage = (provider: string, imageType: string = 'svg') => {
    const iconImgUrl = '/assets/images/game'
    const globImages: GlobImageI = import.meta.glob('~/assets/images/game/*', {
      eager: true,
    })
    return getImageURL(globImages, provider, iconImgUrl, imageType)
  }

  const providers = computed(() => {
    const categoryProviders =
      GAME_LOBBY_PROVIDERS[routeCategory.value as GameLobbyEnum]
    if (!categoryProviders) {
      return []
    }

    return categoryProviders.map((item) => ({
      ...item,
      label: getImage(`label-${item.label}`, 'webp'),
    }))
  })

  const providerOptions = computed(
    () =>
      GAME_LOBBY_PROVIDERS[
        routeCategory.value as keyof typeof GAME_LOBBY_PROVIDERS
      ]?.map(({ key, titleMobile, title, label }) => ({
        key: key || GameLobbyEnum.All,
        title: titleMobile || title,
        label,
      })) || [],
  )

  const isProviderInCategory = (provider?: string) =>
    allGames.value?.some(
      (game: { provider: string | undefined; category: string | string[] }) =>
        game.provider === provider &&
        (game.category?.includes(routeCategory.value) ||
          routeCategory.value === GameLobbyEnum.All),
    )

  const filterProviders = computed(() => {
    return providers.value.filter(
      (item) =>
        item.key === GAME_LOBBY_PROVIDER_ALL || isProviderInCategory(item.key),
    )
  })

  const filterMobileProviders = computed(() => {
    return providerOptions.value?.filter(
      (item: { key: string | undefined }) =>
        item.key === GameLobbyEnum.All || isProviderInCategory(item.key),
    )
  })

  const changeProvider = async (categorySlug: string): Promise<void> => {
    if (categorySlug === GameLobbyEnum.All) {
      categorySlug = ''
    }

    router.replace({
      path: `${RoutesEnum.LobbyGame}/${routeCategory.value}${
        categorySlug ? `/${categorySlug}` : ''
      }`,
    })
  }

  const changeCategory = async (categorySlug: string): Promise<void> => {
    router.replace({
      path: `${RoutesEnum.LobbyGame}/${categorySlug}`,
    })
  }

  onMounted(() => {
    const hasCurrentCategory = GAME_LOBBY_TABS.find(
      (item) => item.key === routeCategory.value,
    )
    const hasCurrentProvider = filterProviders.value.find(
      (item) => item.key === routeProvider.value,
    )
    if (!hasCurrentProvider || !hasCurrentCategory) {
      navigateTo(RoutesEnum.NotFound)
    }
  })

  return {
    routeProvider,
    routeCategory,
    filterProviders,
    filterMobileProviders,
    providers,
    getImage,
    changeCategory,
    changeProvider,
    isProviderInCategory,
  }
}
